import { AUTH_STATES, useAuth } from 'contexts/auth';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Icon, Stack } from 'shell/components';

const defaultForm = {
    email: '',
    password: '',
};

const defaultErrors = {
    email: '',
    password: '',
    login_failed: '',
};

const PageContext = createContext();

const PageProvider = (props) => {
    const { login, signInWithGoogle } = useAuth();
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [emailSubmitted, setEmailSubmitted] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState(defaultErrors);

    const COUNTRY_OPTIONS = useMemo(
        () => [
            {
                key: 'canada',
                value: (
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Icon kind={'canada'} />
                        {t('common:country.canada', 'Canada')}
                    </Stack>
                ),
            },
            {
                key: 'usa',
                value: (
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Icon kind={'usa'} />
                        {t('common:country.usa', 'USA')}
                    </Stack>
                ),
            },
        ],
        [t]
    );

    const [country, setCountry] = useState(COUNTRY_OPTIONS[0].key);

    useEffect(() => {
        if (location.state?.email) setForm((current) => ({ ...current, email: location.state.email }));
    }, [location.state]);

    const getErrorMessage = (key, value) => {
        value = value?.trim();
        switch (key) {
            case 'email':
                return value ? '' : t('errors.email-password-invalid', 'Email or password is invalid');
            case 'password':
                return value ? '' : t('errors.email-password-invalid', 'Email or password is invalid');
            default:
                return '';
        }
    };

    const validate = (key, value) => {
        const errorMessage = getErrorMessage(key, value);
        setErrors((errors) => {
            return {
                ...errors,
                [key]: errorMessage,
            };
        });
    };

    const handleChange = (key, value) => {
        switch (key) {
            case 'email':
            case 'password': {
                setForm((current) => ({ ...current, [key]: value }));
                validate(key, value);
                return;
            }
            case 'country': {
                const currentUrl = window.location.origin;
                setCountry(value);
                if (value === 'canada' && currentUrl !== 'https://app.ensightful.ca') {
                    return window.location.replace('https://app.ensightful.ca');
                } else if (value === 'usa' && currentUrl !== 'https://app.ensightful.us') {
                    return window.location.replace('https://app.ensightful.us');
                }
                return;
            }
            default:
                return;
        }
    };

    const handleClickLogin = async () => {
        if (!emailSubmitted) {
            const emailError = getErrorMessage('email', form.email);
            setErrors((current) => ({ ...current, email: emailError }));
            if (!emailError) setEmailSubmitted(true);
            return;
        }
        try {
            setLoading(true);
            const loginCallback = await login({ username: form.email, password: form.password });
            switch (loginCallback.state) {
                case AUTH_STATES.AUTHENTICATED: {
                    if (loginCallback.route === 'profile-set-up') return history.push('/profile-setup');
                    return history.push('/');
                }
                case AUTH_STATES.CONFIRM_SIGN_UP_REQUIRED: {
                    sessionStorage.setItem('timelygrader.pendingAuthEmail', loginCallback.email);
                    return history.push('/confirm-sign-up', { email: loginCallback.email });
                }
                case AUTH_STATES.NEW_PASSWORD_REQUIRED: {
                    sessionStorage.setItem('timelygrader.pendingAuthEmail', loginCallback.email);
                    return history.push('/update-password', { email: loginCallback.email });
                }
                case AUTH_STATES.PASSWORD_RESET_REQUIRED: {
                    return history.push('/forgot-password', { email: loginCallback.email, requestCode: true });
                }
                case 'profile-set-up': {
                    return history.push('/profile-setup');
                }
                case 'login': {
                    return history.push('/');
                }
                default: {
                    return;
                }
            }
        } catch (e) {
            console.error(e);
            setErrors((errors) => {
                return {
                    ...errors,
                    email: t('errors.email-password-invalid', 'Email or password is invalid'),
                    password: t('errors.email-password-invalid', 'Email or password is invalid'),
                };
            });
        } finally {
            setLoading(false);
        }
    };

    const handleClickButton = (key) => {
        switch (key) {
            case 'login': {
                return handleClickLogin();
            }
            default: {
                return;
            }
        }
    };

    return (
        <PageContext.Provider
            value={{
                loading,
                emailSubmitted,
                setEmailSubmitted,
                country,
                form,
                errors,
                handleChange,
                handleClickButton,
                signInWithGoogle,
                COUNTRY_OPTIONS,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
